// src/components/SEO.jsx
import React from 'react';
import { Helmet } from 'react-helmet-async';

import PropTypes from 'prop-types';

const SITE_URL = 'https://birreriaelsemaforo.com';

// Social scrapers (Facebook, LinkedIn, iMessage) do not run JS, so index.html
// carries this same set of tags marked data-rh="true". Helmet removes any tag
// carrying that attribute and re-emits its own, so the static tags serve as the
// crawler-facing default without duplicating once React mounts. Consequence: a
// tag marked data-rh in index.html MUST be emitted here, or Helmet will strip it
// on mount and never put it back.
const DEFAULT_TITLE =
  'Birreria El Semaforo | Authentic Birria & Tacos in Nampa, Idaho';
const DEFAULT_DESCRIPTION =
  'Bringing bold, authentic Mexican flavors to Nampa: known for our rich birria, unique Birria Tamales, and refreshing Agua Frescas. Order online for pickup, visit our location, or catch our taco truck, Tacos El Semáforo.';
const DEFAULT_KEYWORDS =
  'Birreria, El Semaforo, Nampa, Idaho, birria, birria tacos, Birria Tamales, Agua Frescas, Mexican restaurant, taco truck, online ordering, pickup';
const DEFAULT_OG_IMAGE = '/og-image.jpg';
const OG_IMAGE_ALT =
  'An overhead spread of Birreria El Semaforo dishes: birria quesatacos, tacos, and consomé.';
const OG_IMAGE_WIDTH = '1200';
const OG_IMAGE_HEIGHT = '630';

// og:image must be an absolute URL. Callers may pass either a site-relative path
// or an already-absolute CDN URL, so normalize rather than blindly prefixing.
const toAbsoluteUrl = (url) =>
  /^https?:\/\//i.test(url)
    ? url
    : `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`;

const SEO = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  noindex = false,
  keywords = DEFAULT_KEYWORDS,
  twitterCard = 'summary_large_image',
  twitterCreator,
}) => {
  const canonicalUrl = canonical ? toAbsoluteUrl(canonical) : `${SITE_URL}/`;
  const ogImageUrl = toAbsoluteUrl(ogImage);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* OpenGraph Meta Tags */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content={OG_IMAGE_WIDTH} />
      <meta property="og:image:height" content={OG_IMAGE_HEIGHT} />
      <meta property="og:image:alt" content={OG_IMAGE_ALT} />
      <meta property="og:site_name" content="Birreria El Semaforo" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags — Twitter's spec requires name=, not property= */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={OG_IMAGE_ALT} />
      {twitterCreator && (
        <meta name="twitter:creator" content={twitterCreator} />
      )}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  canonical: PropTypes.string,
  ogImage: PropTypes.string,
  ogType: PropTypes.string,
  twitterCard: PropTypes.string,
  twitterCreator: PropTypes.string,
  noindex: PropTypes.bool,
  keywords: PropTypes.string,
};

export default SEO;
